import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import DealerListingPhotoManager, {
  listingPhotosToEntries,
} from "../../components/dealer/DealerListingPhotoManager";
import {
  AVAILABILITY_STATUSES,
  BODY_TYPE_OPTIONS,
  DOCUMENT_STATUS_LABELS,
  FUEL_OPTIONS,
  SOAT_STATUSES,
  TECNOMECANICA_STATUSES,
  TRANSMISSION_OPTIONS,
  qualityBadgeClass,
  trackInventoryEvent,
} from "../../constants/automapDealerInventoryConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import {
  createDealerListing,
  fetchDealerListing,
  updateDealerListing,
} from "../../services/automapDealerInventoryApi";

const FORM_ID = "dealer-inventory-form";

const SECTION_ORDER = ["photos", "vehicle", "sale", "listing", "documents"];

const EMPTY = {
  make: "",
  model: "",
  version: "",
  year: "",
  priceCop: "",
  mileageKm: "",
  transmission: "automatic",
  fuel: "gasolina",
  bodyType: "SUV",
  color: "",
  city: "",
  neighborhood: "",
  address: "",
  description: "",
  financingAvailable: false,
  availabilityStatus: "unknown",
  engineSize: "",
  doors: "",
  internalPlate: "",
  internalVin: "",
  soatStatus: "not_provided",
  tecnomecanicaStatus: "not_provided",
  ownershipCount: "",
  accidentHistoryNote: "",
  dealerStockNumber: "",
  externalListingUrl: "",
  photos: [],
  qualityScore: null,
  qualityLabel: null,
};

function FormSection({
  emoji,
  title,
  description,
  children,
  aside,
  collapsible = false,
  defaultOpen = true,
  fieldsClassName = "",
}) {
  const { t } = useDealerPortalIntl();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="dealer-panel dealer-inventory-form__section">
      <header className="dealer-inventory-form__section-head">
        <div>
          <h3>
            {emoji ? <span className="dealer-inventory-form__section-emoji">{emoji}</span> : null}
            {title}
          </h3>
          {description && (open || !collapsible) ? (
            <p className="dealer-inventory-form__section-desc">{description}</p>
          ) : null}
        </div>
        <div className="dealer-inventory-form__section-head-actions">
          {aside ? <div className="dealer-inventory-form__section-aside">{aside}</div> : null}
          {collapsible ? (
            <button
              type="button"
              className="dealer-inventory-form__section-toggle"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? t("inventoryForm.collapseSection") : t("inventoryForm.expandSection")}
            </button>
          ) : null}
        </div>
      </header>
      {open || !collapsible ? (
        <div className={`dealer-inventory-form__fields ${fieldsClassName}`.trim()}>{children}</div>
      ) : null}
    </section>
  );
}

function FieldLabel({ label, hint, required, wide, compact, children }) {
  return (
    <label
      className={`dealer-inventory-form__field${wide ? " full" : ""}${compact ? " dealer-inventory-form__field--compact" : ""}${required ? " dealer-inventory-form__field--required" : ""}`}
    >
      <span className="dealer-inventory-form__field-meta">
        <span className="dealer-inventory-form__field-label">
          {label}
          {required ? <span className="dealer-inventory-form__required">*</span> : null}
        </span>
        {hint && !compact ? <span className="dealer-inventory-form__field-hint">{hint}</span> : null}
      </span>
      {children}
      {hint && compact ? <span className="dealer-inventory-form__field-hint">{hint}</span> : null}
    </label>
  );
}

function FormCallout({ children }) {
  return <p className="dealer-inventory-form__callout">{children}</p>;
}

function SectionNav({ sections, activeId, onSelect, photoCount }) {
  return (
    <nav className="dealer-inventory-form__nav" aria-label={sections.navLabel}>
      <p className="dealer-inventory-form__nav-title">{sections.navLabel}</p>
      <ul>
        {sections.items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={activeId === item.id ? "is-active" : undefined}
              onClick={() => onSelect(item.id)}
            >
              <span className="dealer-inventory-form__nav-emoji" aria-hidden="true">
                {item.emoji}
              </span>
              <span className="dealer-inventory-form__nav-label">{item.label}</span>
              {item.id === "photos" && photoCount > 0 ? (
                <span className="dealer-inventory-form__nav-badge">{photoCount}</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function FormActionsRail({ cancelPath, saving, isEdit, t, onSave }) {
  return (
    <aside className="dealer-inventory-form__rail" aria-label={t("inventoryForm.actionsTitle")}>
      <div className="dealer-inventory-form__rail-card dealer-inventory-form__rail-card--actions">
        <p className="dealer-inventory-form__rail-title">{t("inventoryForm.actionsTitle")}</p>
        <div className="dealer-inventory-form__rail-actions">
          <button
            type="submit"
            form={FORM_ID}
            className="dealer-portal-btn primary"
            disabled={saving}
          >
            ✅ {t("inventoryForm.save")}
          </button>
          {!isEdit ? (
            <button
              type="button"
              className="dealer-portal-btn primary"
              disabled={saving}
              onClick={() => onSave({ publish: true })}
            >
              🚀 {t("inventoryForm.saveAndPublish")}
            </button>
          ) : null}
          <button
            type="button"
            className="dealer-portal-btn"
            disabled={saving}
            onClick={() => onSave({ saveAsDraft: true })}
          >
            💾 {t("inventoryForm.saveDraft")}
          </button>
          <Link className="dealer-portal-btn" to={cancelPath}>
            {t("common.cancel")}
          </Link>
        </div>
      </div>
    </aside>
  );
}

export default function DealerInventoryFormPage() {
  const { listingId } = useParams();
  const isEdit = Boolean(listingId);
  const navigate = useNavigate();
  const { dealerId } = useOutletContext();
  const { t, formatListingStatus, translateError } = useDealerPortalIntl();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState(SECTION_ORDER[0]);
  const sectionRefs = useRef({});

  const section = (key) => ({
    emoji: t(`inventoryForm.sectionEmojis.${key}`),
    title: t(`inventoryForm.sections.${key}`),
    description: t(`inventoryForm.sectionDescriptions.${key}`),
  });

  const hint = (field) => t(`inventoryForm.fieldHints.${field}`);

  const photoLabels = useMemo(
    () => ({
      helper: t("inventoryForm.photos.helper"),
      empty: t("inventoryForm.photos.empty"),
      primaryBadge: t("inventoryForm.photos.primaryBadge"),
      addUrl: t("inventoryForm.photos.addUrl"),
      orAddUrl: t("inventoryForm.photos.orAddUrl"),
      addUrlPlaceholder: t("inventoryForm.photos.addUrlPlaceholder"),
      uploadHint: t("inventoryForm.photos.uploadHint"),
      uploadFiles: t("inventoryForm.photos.uploadFiles"),
      uploadingFiles: t("inventoryForm.photos.uploadingFiles"),
      uploadedFileNote: t("inventoryForm.photos.uploadedFileNote"),
      uploadedFilePlaceholder: t("inventoryForm.photos.uploadedFilePlaceholder"),
      moveUp: t("inventoryForm.photos.moveUp"),
      moveDown: t("inventoryForm.photos.moveDown"),
      setPrimary: t("inventoryForm.photos.setPrimary"),
      remove: t("inventoryForm.photos.remove"),
      count: t("inventoryForm.photos.count"),
      invalidUrl: t("inventoryForm.photos.invalidUrl"),
      tooMany: t("inventoryForm.photos.tooMany"),
      tooLarge: t("inventoryForm.photos.tooLarge"),
      invalidType: t("inventoryForm.photos.invalidType"),
      emptyFile: t("inventoryForm.photos.emptyFile"),
      uploadFailed: t("inventoryForm.photos.uploadFailed"),
      sectionsPhotos: t("inventoryForm.sections.photos"),
    }),
    [t]
  );

  const navSections = useMemo(
    () => ({
      navLabel: t("inventoryForm.sectionNavTitle"),
      items: SECTION_ORDER.map((id) => ({
        id,
        emoji: t(`inventoryForm.sectionEmojis.${id}`),
        label: t(`inventoryForm.sections.${id}`),
      })),
    }),
    [t]
  );

  const documentStatusLabel = (status) =>
    t(`inventoryForm.documentStatus.${status}`) || DOCUMENT_STATUS_LABELS[status] || status;

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchDealerListing(dealerId, listingId);
        const listing = data.listing;
        if (cancelled || !listing) return;
        setForm({
          ...EMPTY,
          ...listing,
          year: listing.year ?? "",
          priceCop: listing.priceCop ?? "",
          mileageKm: listing.mileageKm ?? "",
          doors: listing.doors ?? "",
          ownershipCount: listing.ownershipCount ?? "",
          photos: listingPhotosToEntries(listing.photos),
          qualityScore: listing.qualityScore ?? null,
          qualityLabel: listing.qualityLabel ?? null,
        });
      } catch (err) {
        setError(translateError(err.message, "generic") || err.message || "No se pudo cargar el vehículo.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealerId, isEdit, listingId, translateError]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveSection(visible[0].target.id.replace("section-", ""));
        }
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0.1, 0.35, 0.6] }
    );

    for (const id of SECTION_ORDER) {
      const node = sectionRefs.current[id];
      if (node) observer.observe(node);
    }

    return () => observer.disconnect();
  }, [loading]);

  const scrollToSection = (id) => {
    const node = sectionRefs.current[id];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const buildPayload = (options = {}) => ({
    make: form.make,
    model: form.model,
    version: form.version,
    year: Number(form.year),
    priceCop: Number(form.priceCop),
    mileageKm: Number(form.mileageKm),
    transmission: form.transmission,
    fuel: form.fuel,
    bodyType: form.bodyType,
    color: form.color,
    city: form.city,
    neighborhood: form.neighborhood,
    address: form.address,
    description: form.description,
    financingAvailable: form.financingAvailable,
    availabilityStatus: form.availabilityStatus,
    engineSize: form.engineSize,
    doors: form.doors ? Number(form.doors) : null,
    internalPlate: form.internalPlate,
    internalVin: form.internalVin,
    soatStatus: form.soatStatus,
    tecnomecanicaStatus: form.tecnomecanicaStatus,
    ownershipCount: form.ownershipCount ? Number(form.ownershipCount) : null,
    accidentHistoryNote: form.accidentHistoryNote,
    dealerStockNumber: form.dealerStockNumber,
    externalListingUrl: form.externalListingUrl,
    photos: form.photos
      .map((photo) => photo.url.trim())
      .filter(Boolean)
      .map((url, index) => ({
        url,
        isPrimary: index === 0,
        sortOrder: index,
      })),
    ...options,
  });

  const save = async (options = {}) => {
    setSaving(true);
    setError("");
    try {
      const payload = buildPayload(options);
      if (isEdit) {
        await updateDealerListing(dealerId, listingId, payload);
        trackInventoryEvent("inventory_listing_updated", { listingId, dealerId });
        navigate(`/v/automap/dealer/inventario/${listingId}`);
      } else {
        const data = await createDealerListing(dealerId, payload);
        trackInventoryEvent("inventory_listing_created", { listingId: data.listing?.id, dealerId });
        if (options.publish) trackInventoryEvent("inventory_listing_published", { dealerId });
        navigate(`/v/automap/dealer/inventario/${data.listing.id}`);
      }
    } catch (err) {
      setError(translateError(err.message, "generic") || err.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const vehicleTitle = [form.make, form.model, form.version, form.year].filter(Boolean).join(" ");
  const cancelPath = isEdit ? `/v/automap/dealer/inventario/${listingId}` : "/v/automap/dealer/inventario";

  const bindSectionRef = (id) => (node) => {
    sectionRefs.current[id] = node;
  };

  if (loading) {
    return <p className="dealer-state-box">{t("inventoryForm.loading")}</p>;
  }

  return (
    <div className="dealer-inventory-form">
      <header className="dealer-inventory-form__header">
        <div>
          <Link className="dealer-inventory-form__back muted" to={cancelPath}>
            {isEdit ? t("inventoryForm.backToListing") : t("inventoryForm.backToInventory")}
          </Link>
          <h2>
            {isEdit ? "✏️ " : "➕ "}
            {isEdit ? t("inventoryForm.editTitle") : t("inventoryForm.addTitle")}
          </h2>
          {vehicleTitle ? <p className="dealer-inventory-form__subtitle">{vehicleTitle}</p> : null}
          <p className="dealer-inventory-form__intro">{t("inventoryForm.intro")}</p>
        </div>
        {isEdit && form.qualityScore != null ? (
          <div className="dealer-inventory-form__quality">
            <span className="muted">{t("inventoryForm.qualityScore")}</span>
            <span className={`admin-badge ${qualityBadgeClass(form.qualityScore)}`}>
              ⭐ {form.qualityScore} · {form.qualityLabel}
            </span>
          </div>
        ) : null}
      </header>

      <div className="dealer-inventory-form__nav-mobile">
        <SectionNav
          sections={navSections}
          activeId={activeSection}
          onSelect={scrollToSection}
          photoCount={form.photos.length}
        />
      </div>

      {error ? <p className="dealer-state-box dealer-inventory-form__error">{error}</p> : null}

      <div className="dealer-inventory-form__layout">
        <div className="dealer-inventory-form__nav-desktop">
          <SectionNav
            sections={navSections}
            activeId={activeSection}
            onSelect={scrollToSection}
            photoCount={form.photos.length}
          />
        </div>

        <form
          id={FORM_ID}
          className="dealer-inventory-form__main"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <div ref={bindSectionRef("photos")} id="section-photos" className="dealer-inventory-form__section-anchor">
            <FormSection {...section("photos")}>
              <div className="dealer-inventory-form__field full">
                <DealerListingPhotoManager
                  photos={form.photos}
                  disabled={saving}
                  labels={photoLabels}
                  onChange={(photos) => update("photos", photos)}
                />
              </div>
            </FormSection>
          </div>

          <div ref={bindSectionRef("vehicle")} id="section-vehicle" className="dealer-inventory-form__section-anchor">
            <FormSection {...section("vehicle")} fieldsClassName="dealer-inventory-form__fields--dense">
              <FieldLabel label={t("inventoryForm.fields.make")} hint={hint("make")} required compact>
                <input value={form.make} onChange={(event) => update("make", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.model")} hint={hint("model")} required compact>
                <input value={form.model} onChange={(event) => update("model", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.version")} hint={hint("version")} compact>
                <input value={form.version} onChange={(event) => update("version", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.year")} hint={hint("year")} required compact>
                <input type="number" value={form.year} onChange={(event) => update("year", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.bodyType")} hint={hint("bodyType")} required compact>
                <select value={form.bodyType} onChange={(event) => update("bodyType", event.target.value)}>
                  {BODY_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.transmission")} hint={hint("transmission")} required compact>
                <select value={form.transmission} onChange={(event) => update("transmission", event.target.value)}>
                  {TRANSMISSION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.fuel")} hint={hint("fuel")} required compact>
                <select value={form.fuel} onChange={(event) => update("fuel", event.target.value)}>
                  {FUEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.color")} hint={hint("color")} compact>
                <input value={form.color} onChange={(event) => update("color", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.engineSize")} hint={hint("engineSize")} compact>
                <input value={form.engineSize} onChange={(event) => update("engineSize", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.doors")} hint={hint("doors")} compact>
                <input type="number" value={form.doors} onChange={(event) => update("doors", event.target.value)} />
              </FieldLabel>
            </FormSection>
          </div>

          <div ref={bindSectionRef("sale")} id="section-sale" className="dealer-inventory-form__section-anchor">
            <FormSection {...section("sale")} fieldsClassName="dealer-inventory-form__fields--dense">
              <FieldLabel label={t("inventoryForm.fields.priceCop")} hint={hint("priceCop")} required compact>
                <input type="number" value={form.priceCop} onChange={(event) => update("priceCop", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.mileageKm")} hint={hint("mileageKm")} required compact>
                <input type="number" value={form.mileageKm} onChange={(event) => update("mileageKm", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.availabilityStatus")} hint={hint("availabilityStatus")} compact>
                <select
                  value={form.availabilityStatus}
                  onChange={(event) => update("availabilityStatus", event.target.value)}
                >
                  {AVAILABILITY_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {formatListingStatus(status)}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <div className="dealer-inventory-form__field dealer-inventory-form__field--checkbox-row">
                <label className="dealer-inventory-form__checkbox">
                  <input
                    type="checkbox"
                    checked={form.financingAvailable}
                    onChange={(event) => update("financingAvailable", event.target.checked)}
                  />
                  <span>{t("inventoryForm.fields.financingAvailable")}</span>
                </label>
                <span className="dealer-inventory-form__field-hint">{hint("financingAvailable")}</span>
              </div>
              <FieldLabel label={t("inventoryForm.fields.city")} hint={hint("city")} required compact>
                <input value={form.city} onChange={(event) => update("city", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.neighborhood")} hint={hint("neighborhood")} compact>
                <input value={form.neighborhood} onChange={(event) => update("neighborhood", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.address")} hint={hint("address")} wide>
                <input value={form.address} onChange={(event) => update("address", event.target.value)} />
              </FieldLabel>
            </FormSection>
          </div>

          <div ref={bindSectionRef("listing")} id="section-listing" className="dealer-inventory-form__section-anchor">
            <FormSection {...section("listing")}>
              <FieldLabel label={t("inventoryForm.fields.description")} hint={hint("description")} wide>
                <textarea
                  value={form.description}
                  onChange={(event) => update("description", event.target.value)}
                  rows={6}
                />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.dealerStockNumber")} hint={hint("dealerStockNumber")} compact>
                <input
                  value={form.dealerStockNumber}
                  onChange={(event) => update("dealerStockNumber", event.target.value)}
                />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.externalListingUrl")} hint={hint("externalListingUrl")} wide>
                <input
                  type="url"
                  value={form.externalListingUrl}
                  onChange={(event) => update("externalListingUrl", event.target.value)}
                />
              </FieldLabel>
            </FormSection>
          </div>

          <div ref={bindSectionRef("documents")} id="section-documents" className="dealer-inventory-form__section-anchor">
            <FormSection
              {...section("documents")}
              collapsible
              defaultOpen={false}
              fieldsClassName="dealer-inventory-form__fields--dense"
            >
              <FormCallout>{t("inventoryForm.documentPrivacyNote")}</FormCallout>
              <FieldLabel label={t("inventoryForm.fields.internalPlate")} hint={hint("internalPlate")} compact>
                <input value={form.internalPlate} onChange={(event) => update("internalPlate", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.internalVin")} hint={hint("internalVin")} compact>
                <input value={form.internalVin} onChange={(event) => update("internalVin", event.target.value)} />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.soatStatus")} hint={hint("soatStatus")} compact>
                <select value={form.soatStatus} onChange={(event) => update("soatStatus", event.target.value)}>
                  {SOAT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {documentStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.tecnomecanicaStatus")} hint={hint("tecnomecanicaStatus")} compact>
                <select
                  value={form.tecnomecanicaStatus}
                  onChange={(event) => update("tecnomecanicaStatus", event.target.value)}
                >
                  {TECNOMECANICA_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {documentStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.ownershipCount")} hint={hint("ownershipCount")} compact>
                <input
                  type="number"
                  min="0"
                  value={form.ownershipCount}
                  onChange={(event) => update("ownershipCount", event.target.value)}
                />
              </FieldLabel>
              <FieldLabel label={t("inventoryForm.fields.accidentHistoryNote")} hint={hint("accidentHistoryNote")} wide>
                <textarea
                  value={form.accidentHistoryNote}
                  onChange={(event) => update("accidentHistoryNote", event.target.value)}
                  rows={3}
                />
              </FieldLabel>
            </FormSection>
          </div>
        </form>

        <div className="dealer-inventory-form__rail-wrap">
          <aside className="dealer-inventory-form__rail" aria-label={t("inventoryForm.sidebarTitle")}>
            <div className="dealer-inventory-form__rail-card dealer-inventory-form__rail-card--tips">
              <strong>{t("inventoryForm.tipsTitle")}</strong>
              <ul>
                <li>{t("inventoryForm.tips.photos")}</li>
                <li>{t("inventoryForm.tips.price")}</li>
                <li>{t("inventoryForm.tips.docs")}</li>
              </ul>
            </div>
          </aside>
          <FormActionsRail
            cancelPath={cancelPath}
            saving={saving}
            isEdit={isEdit}
            t={t}
            onSave={save}
          />
        </div>
      </div>

      <footer className="dealer-inventory-form__footer-mobile">
        <button type="submit" form={FORM_ID} className="dealer-portal-btn primary" disabled={saving}>
          ✅ {t("inventoryForm.save")}
        </button>
        <Link className="dealer-portal-btn" to={cancelPath}>
          {t("common.cancel")}
        </Link>
      </footer>
    </div>
  );
}
